/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { ParentSize } from "@visx/responsive";
import dayjs from "dayjs";
import { Box, Spacer, theme } from "@nordcloud/gnui";
import { useGlobalCostAllocationQuery } from "~/generated/graphql";
import {
  DataContainer,
  DataItem,
  DateRange,
  EstatePeriod,
  EstatePeriod as Period,
  PeriodSelector,
  Separator,
} from "~/components";
import { dateFormat } from "~/constants";
import { isNotNil } from "~/tools";
import { CostAllocationChart } from "./CostAllocationChart";
import { CostAllocationLegend } from "./CostAllocationLegend";
import { UnallocatedSummary } from "./UnallocatedSummary";

export function CostAllocationV2() {
  const [scale, setScale] = useState<{
    period: Period;
    range: DateRange | undefined;
  }>({
    period: Period.CURRENT_MONTH,
    range: undefined,
  });
  const [time, setTime] = useState({
    startDate: dayjs().startOf("month").format(dateFormat.shortDate),
    endDate: dayjs().format(dateFormat.shortDate),
  });

  const { data, loading } = useGlobalCostAllocationQuery({
    variables: { startDate: time.startDate, endDate: time.endDate },
  });

  const costAllocation = data?.globalMetrics?.costAllocation;

  const handlePeriodChange = (
    selectedPeriod: EstatePeriod,
    selectedRange?: DateRange
  ) => {
    setScale({
      period: selectedPeriod,
      range: selectedRange,
    });

    if (selectedPeriod === Period.RANGE && isNotNil(selectedRange)) {
      setTime({
        startDate: selectedRange.from,
        endDate: selectedRange.to,
      });
    }

    if (selectedPeriod === Period.CURRENT_MONTH) {
      setTime({
        startDate: dayjs().startOf("month").format(dateFormat.shortDate),
        endDate: dayjs().format(dateFormat.shortDate),
      });
    }
    if (selectedPeriod === EstatePeriod.PREVIOUS_MONTH) {
      setTime({
        startDate: dayjs()
          .subtract(1, "month")
          .startOf("month")
          .format(dateFormat.shortDate),
        endDate: dayjs()
          .subtract(1, "month")
          .endOf("month")
          .format(dateFormat.shortDate),
      });
    }
  };

  return (
    <Box spacing="spacing04" mb={theme.spacing.spacing04}>
      <section>
        <DataContainer hasBorder={false}>
          <DataItem
            grow
            padding="0"
            weight="medium"
            size="md"
            value="Cost Allocation"
            description="Allows to track the percentage of the cost allocation in the applications. We record a daily value of % of the allocated total cost."
          />
        </DataContainer>
      </section>
      <Box
        boxStyle="lightGrey"
        p={`${theme.spacing.spacing04} ${theme.spacing.spacing02}`}
      >
        <PeriodSelector
          period={scale.period}
          range={scale.range}
          onChange={handlePeriodChange}
        />
      </Box>
      <Spacer height={theme.spacing.spacing02} />
      <ParentSize>
        {(parent) => (
          <CostAllocationChart
            startDate={time.startDate}
            endDate={time.endDate}
            width={parent.width}
            height={250}
            data={costAllocation?.timeSeries ?? []}
            loading={loading}
          />
        )}
      </ParentSize>
      <Spacer height={theme.spacing.spacing02} />
      <CostAllocationLegend />
      <Spacer height={theme.spacing.spacing04} />
      <Separator />
      <Spacer height={theme.spacing.spacing04} />
      <UnallocatedSummary />
    </Box>
  );
}
